import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, KeyRound, ShieldCheck, Save, RotateCcw, Upload, Trophy,
  ChevronDown, ClipboardList, Rocket, ArrowLeft, Loader2,
} from "lucide-react";
import { GROUPS } from "../data/groups";
import { ORBITS } from "../data/orbits";

const API = "/api";

const inputCls =
  "w-full rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[#4cc9f0]";

const Section = ({ icon: Icon, title, desc, testid, children }) => (
  <section data-testid={testid} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
    <h2 className="font-heading flex items-center gap-2 text-xl font-semibold text-white">
      <Icon className="h-5 w-5 text-[#4cc9f0]" /> {title}
    </h2>
    {desc && <p className="mt-1 text-sm leading-relaxed text-white/60">{desc}</p>}
    <div className="mt-5">{children}</div>
  </section>
);

const GateEditor = ({ orbit, cfg, pw, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [gatePassword, setGatePassword] = useState(cfg.gate_password || orbit.password);
  const [title, setTitle] = useState(cfg.title || orbit.reveal.title);
  const [desc, setDesc] = useState(cfg.desc || orbit.reveal.desc);
  const [override, setOverride] = useState(cfg.input_override || "");
  const [images, setImages] = useState(cfg.images || {});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/gates/${orbit.week}`, {
        password: pw,
        gate_password: gatePassword,
        title,
        desc,
        input_override: override,
      });
      toast.success(`${orbit.title} tersimpan.`);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan gerbang.");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (slot, file) => {
    if (!file || uploading) return;
    setUploading(slot);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(
        `${API}/admin/gates/${orbit.week}/image?slot=${slot}&password=${encodeURIComponent(pw)}`,
        form
      );
      setImages((p) => ({ ...p, [String(slot)]: data.path }));
      toast.success(`Gambar ${slot} ${orbit.title} terunggah.`);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengunggah gambar.");
    } finally {
      setUploading(0);
    }
  };

  return (
    <div data-testid={`admin-gate-${orbit.week}`} className="rounded-2xl border border-white/10 bg-white/[0.03]">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`admin-gate-toggle-${orbit.week}`}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <span className="font-heading text-sm font-medium text-white">
          {orbit.title}
          <span className="ml-2 text-xs font-normal text-white/50">{orbit.dates}</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-white/60 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="space-y-4 border-t border-white/10 px-5 py-5">
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-widest text-white/60">
              Password Gerbang
            </label>
            <input
              value={gatePassword}
              onChange={(e) => setGatePassword(e.target.value)}
              data-testid={`admin-gate-password-${orbit.week}`}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-widest text-white/60">
              Judul Reveal
            </label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`admin-gate-title-${orbit.week}`} className={inputCls} />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-widest text-white/60">
              Deskripsi Reveal
            </label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={2}
              data-testid={`admin-gate-desc-${orbit.week}`}
              className={`${inputCls} resize-none`}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-widest text-white/60">
              Status Kolom Password
            </label>
            <select
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              data-testid={`admin-gate-override-${orbit.week}`}
              className="w-full rounded-xl border border-white/20 bg-[#1a1145] px-4 py-2.5 text-sm text-white outline-none focus:border-[#4cc9f0]"
            >
              <option value="">Otomatis (ikuti Papan Status Clue)</option>
              <option value="open">Paksa Terbuka</option>
              <option value="locked">Paksa Terkunci</option>
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-widest text-white/60">
              Gambar Starterpack (urut 1 → 3)
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((slot) => {
                const path = images[String(slot)];
                const staticImg = orbit.starterpackImages?.[slot - 1];
                const src = path ? `/api/gates/files/${path}` : staticImg;
                return (
                  <label
                    key={slot}
                    data-testid={`admin-gate-image-${orbit.week}-${slot}`}
                    className="group relative flex aspect-[1414/2000] cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-white/25 bg-white/[0.04] transition-colors hover:border-[#4cc9f0]"
                  >
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        upload(slot, e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                    {src ? (
                      <img src={src} alt={`Slot ${slot}`} className="absolute inset-0 h-full w-full object-cover" />
                    ) : null}
                    <span className="relative z-10 flex flex-col items-center gap-1 rounded-full bg-[#0A0A1A]/70 px-3 py-2 text-[11px] font-medium text-white backdrop-blur">
                      {uploading === slot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      {src ? `Ganti ${slot}` : `Upload ${slot}`}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid={`admin-gate-save-${orbit.week}`}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#4cc9f0] py-2.5 font-heading text-sm font-medium text-[#0A0A1A] transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> {saving ? "Menyimpan..." : `Simpan ${orbit.title}`}
          </button>
        </div>
      )}
    </div>
  );
};

export const AdminPanel = () => {
  const [pw, setPw] = useState("");
  const pwRef = useRef("");
  const [error, setError] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [gates, setGates] = useState({});
  const [clues, setClues] = useState({});
  const [clueItems, setClueItems] = useState({});
  const [savingClues, setSavingClues] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [awardWeek, setAwardWeek] = useState("");
  const [awardTeam, setAwardTeam] = useState(GROUPS[0].codename);
  const [awardMvp, setAwardMvp] = useState("");
  const [savingAward, setSavingAward] = useState(false);

  const loadState = (data) => {
    const gmap = {};
    (data.gates || []).forEach((g) => (gmap[g.week] = g));
    setGates(gmap);
    setClues(data.clues || {});
    const cmap = {};
    GROUPS.forEach((g) => {
      cmap[g.codename] = {
        clue: data.clues?.[g.codename]?.clue || "",
        answer: data.clues?.[g.codename]?.answer || "",
      };
    });
    setClueItems(cmap);
  };

  const refresh = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/state`, { password: pwRef.current });
      setClues(data.clues || {});
      const gmap = {};
      (data.gates || []).forEach((g) => (gmap[g.week] = g));
      setGates(gmap);
    } catch {
      // abaikan; form tetap memakai data terakhir
    }
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/state`, { password: pw.trim() });
      pwRef.current = pw.trim();
      loadState(data);
      setAuthed(true);
      setError(false);
    } catch {
      setError(true);
      setTimeout(() => setError(false), 2500);
    }
  };

  const saveClues = async () => {
    if (savingClues) return;
    setSavingClues(true);
    try {
      await axios.post(`${API}/admin/clues`, {
        password: pwRef.current,
        items: GROUPS.map((g) => ({
          group: g.codename,
          clue: clueItems[g.codename]?.clue || "",
          answer: clueItems[g.codename]?.answer || "",
        })),
      });
      toast.success("Clue mingguan tersimpan dan langsung berlaku.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan clue.");
    } finally {
      setSavingClues(false);
    }
  };

  const resetBoard = async () => {
    if (!confirmReset) {
      setConfirmReset(true);
      setTimeout(() => setConfirmReset(false), 3500);
      return;
    }
    setConfirmReset(false);
    try {
      await axios.post(`${API}/clues/reset`, { password: pwRef.current });
      toast("Papan clue direset — siap untuk minggu baru.");
    } catch {
      toast.error("Gagal mereset papan.");
    }
  };

  const saveAward = async (e) => {
    e.preventDefault();
    if (!awardWeek || !awardMvp.trim() || savingAward) return;
    setSavingAward(true);
    try {
      await axios.post(`${API}/awards`, {
        password: pwRef.current,
        week: Number(awardWeek),
        team: awardTeam,
        mvp: awardMvp.trim(),
      });
      toast.success(`Team & MVP Minggu ${awardWeek} tersimpan.`);
      setAwardWeek("");
      setAwardMvp("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan data.");
    } finally {
      setSavingAward(false);
    }
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A1A] px-6" data-testid="admin-login">
        <form
          onSubmit={login}
          className="glass w-full max-w-sm rounded-3xl p-8"
        >
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
            <Lock className="h-5 w-5 text-[#4cc9f0]" />
          </span>
          <h1 className="font-heading mt-4 text-center text-2xl font-semibold text-white">
            Panel <span className="aurora-text">Panitia</span>
          </h1>
          <p className="mt-1 text-center text-xs uppercase tracking-widest text-white/50">GRAFENA 2026</p>
          <div className="relative mt-6">
            <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Password panitia"
              autoFocus
              data-testid="admin-password-input"
              className={`w-full rounded-full border bg-white/10 py-3 pl-10 pr-4 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[#4cc9f0] ${
                error ? "border-[#f72585]" : "border-white/20"
              }`}
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-2 text-center text-xs font-medium text-[#f72585]">
              Password salah.
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            className="mt-4 w-full rounded-full bg-[#4cc9f0] py-3 font-heading text-sm font-medium text-[#0A0A1A] transition-transform duration-300 hover:scale-[1.02]"
          >
            Masuk Panel
          </button>
          <a href="/" data-testid="admin-back-link" className="mt-4 flex items-center justify-center gap-1.5 text-xs text-white/50 transition-colors hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Kembali ke website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A1A] px-6 py-10" data-testid="admin-panel">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-white sm:text-3xl">
              Panel <span className="aurora-text">Panitia</span>
            </h1>
            <p className="mt-1 flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/50">
              <ShieldCheck className="h-3.5 w-3.5 text-[#4cc9f0]" /> Perubahan langsung berlaku untuk semua pengunjung
            </p>
          </div>
          <a
            href="/"
            data-testid="admin-back-link"
            className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-xs text-white/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Website
          </a>
        </div>

        <div className="mt-8 space-y-8">
          <Section
            icon={Rocket}
            title="Kelola Gerbang Kunci Orbit"
            desc="Atur password, judul & deskripsi reveal, 3 gambar starterpack, dan status kolom password tiap gerbang."
            testid="admin-section-gates"
          >
            <div className="space-y-3">
              {ORBITS.map((o) => (
                <GateEditor key={o.week} orbit={o} cfg={gates[o.week] || {}} pw={pwRef.current} onRefresh={refresh} />
              ))}
            </div>
          </Section>

          <Section
            icon={ClipboardList}
            title="Kelola Clue Mingguan"
            desc="Clue Tempat tampil di Papan Status Clue. Jawaban yang benar dipakai untuk validasi submit tiap kelompok (tidak case-sensitive, spasi diabaikan)."
            testid="admin-section-clues"
          >
            <div className="space-y-4">
              {GROUPS.map((g) => (
                <div key={g.codename} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`admin-clue-${g.codename.toLowerCase()}`}>
                  <p className="font-heading text-sm font-semibold text-white">{g.codename}</p>
                  <input
                    value={clueItems[g.codename]?.clue || ""}
                    onChange={(e) => setClueItems((p) => ({ ...p, [g.codename]: { ...p[g.codename], clue: e.target.value } }))}
                    placeholder="Clue Tempat (tampil ke publik)"
                    data-testid={`admin-clue-text-${g.codename.toLowerCase()}`}
                    className={`${inputCls} mt-2.5`}
                  />
                  <input
                    value={clueItems[g.codename]?.answer || ""}
                    onChange={(e) => setClueItems((p) => ({ ...p, [g.codename]: { ...p[g.codename], answer: e.target.value } }))}
                    placeholder="Jawaban clue yang benar (rahasia)"
                    data-testid={`admin-clue-answer-${g.codename.toLowerCase()}`}
                    className={`${inputCls} mt-2`}
                  />
                </div>
              ))}
              <button
                onClick={saveClues}
                disabled={savingClues}
                data-testid="admin-clues-save"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#4cc9f0] py-3 font-heading text-sm font-medium text-[#0A0A1A] transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
              >
                <Save className="h-4 w-4" /> {savingClues ? "Menyimpan..." : "Simpan Semua Clue"}
              </button>
            </div>
          </Section>

          <Section
            icon={RotateCcw}
            title="Kelola Papan Status Clue"
            desc="Kembalikan checklist ketujuh kelompok ke 'belum submit' saat memulai minggu baru."
            testid="admin-section-board"
          >
            <button
              onClick={resetBoard}
              data-testid="admin-board-reset"
              className={`flex w-full items-center justify-center gap-2 rounded-full py-3 font-heading text-sm font-medium transition-transform duration-300 hover:scale-[1.01] ${
                confirmReset ? "bg-[#f72585] text-white" : "border border-[#f72585]/50 bg-[#f72585]/10 text-[#f72585]"
              }`}
            >
              <RotateCcw className="h-4 w-4" />
              {confirmReset ? "Yakin? Klik lagi untuk reset" : "Reset Papan Clue"}
            </button>
          </Section>

          <Section
            icon={Trophy}
            title="Kelola Team & MVP of the Week"
            desc="Simpan data minggu aktif — minggu sebelumnya otomatis pindah ke Riwayat."
            testid="admin-section-awards"
          >
            <form onSubmit={saveAward} className="space-y-3">
              <input
                type="number"
                min="1"
                max="8"
                value={awardWeek}
                onChange={(e) => setAwardWeek(e.target.value)}
                placeholder="Minggu ke- (1-8)"
                data-testid="admin-award-week"
                className={inputCls}
              />
              <select
                value={awardTeam}
                onChange={(e) => setAwardTeam(e.target.value)}
                data-testid="admin-award-team"
                className="w-full rounded-xl border border-white/20 bg-[#1a1145] px-4 py-2.5 text-sm text-white outline-none focus:border-[#4cc9f0]"
              >
                {GROUPS.map((g) => (
                  <option key={g.codename} value={g.codename}>
                    Team: {g.codename}
                  </option>
                ))}
              </select>
              <input
                value={awardMvp}
                onChange={(e) => setAwardMvp(e.target.value)}
                placeholder="Nama MVP of the Week"
                data-testid="admin-award-mvp"
                className={inputCls}
              />
              <button
                type="submit"
                disabled={savingAward}
                data-testid="admin-award-save"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#4cc9f0] py-3 font-heading text-sm font-medium text-[#0A0A1A] transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
              >
                <Save className="h-4 w-4" /> {savingAward ? "Menyimpan..." : "Simpan Team & MVP"}
              </button>
            </form>
          </Section>
        </div>
      </div>
    </div>
  );
};
